import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { appTypography } from "../theme/appTypography";
import {
    CssBaseline, AppBar, Toolbar, IconButton, Typography, Box, Button,
    Drawer, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper,
    Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
    useMediaQuery, ThemeProvider, createTheme, Tooltip, Grid, MenuItem, TextField, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, ListItemAvatar, Avatar, Chip,
    Select, FormControl, InputLabel, Snackbar, TableContainer
} from "@mui/material";
import {
    Menu as MenuIcon, Dashboard as DashboardIcon, Mail as MailIcon, Inbox as InboxIcon,
    Warning as WarningIcon, People as PeopleIcon,
    QueryStats as QueryStatsIcon, DownloadOutlined as DownloadOutlinedIcon,
    Logout as LogoutIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon, Speed,
    AddTask as AddTaskIcon, Send as SendIcon, Notifications as NotificationsIcon,
    PersonAdd as PersonAddIcon, LockReset as LockResetIcon, Edit as EditIcon, Delete as DeleteIcon,
    Campaign as CampaignIcon, ManageAccounts as ManageAccountsIcon,
    Badge as BadgeIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend as RechartsLegend } from "recharts";
import { useLocation, useNavigate } from "react-router-dom";

// Updated import paths with .js extension
import api from "../axiosConfig.js";
import { AuthContext } from "../AuthContext.js";
import { collection, query, onSnapshot, orderBy, doc, addDoc, serverTimestamp, updateDoc } from "firebase/firestore";
// Updated import path with .js extension
import { db as firestoreDb } from "../firebase.js";
import Performance from "./Perfomance";
import WhatsAppLinesPage from "../pages/WhatsAppLinesPage";
import BroadcastBudgetPanel from "./BroadcastBudgetPanel";
import MassEmailMonitoringSection from "./MassEmailMonitoringSection";
import MassEmailCompanySignatureSection from "./MassEmailCompanySignatureSection";


const DRAWER_WIDTH_FULL = 260;
const DRAWER_WIDTH_MINI = 80;
const HEADER_HEIGHT = 70;
const GUTTER = 24;
const MIN_HANDLED = 5;

const globalAppId = "1:240171385312:web:16ba555d363cf85bc62b85"; // A-OK

const round = (value, decimals = 2) => {
    if (typeof value !== 'number' || typeof decimals !== 'number') return 0;
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
};

const MotionTableRow = motion(TableRow);

// This component now filters from 'allUsers' to get only consultants
function ConsultantLeaderboard({ inquiries, allUsers, firestoreInquiriesLoaded }) {
    const [leaderboard, setLeaderboard] = useState([]);

    const allConsultants = useMemo(() => allUsers.filter(u => u.role === 'Consultant'), [allUsers]);

    useEffect(() => {
        if (!firestoreInquiriesLoaded || allConsultants.length === 0) {
            setLeaderboard([]);
            return;
        }

        const consultantStats = allConsultants.map(consultant => {
            const assignedInquiries = inquiries.filter(inq => String(inq.consultantId) === String(consultant.id));
            const handledCount = assignedInquiries.filter(inq => inq.status && !inq.status.includes('new')).length;

            const responseTimes = assignedInquiries
                .map(inq => {
                    const createdAt = inq.createdAt?.toDate ? inq.createdAt.toDate() : null;
                    const openedAt = inq.consultantOpenedAt?.toDate ? inq.consultantOpenedAt.toDate() : null;
                    if (createdAt && openedAt && openedAt > createdAt) {
                        return (openedAt.getTime() - createdAt.getTime()) / 1000; // in seconds
                    }
                    return null;
                })
                .filter(time => time !== null);

            const avgResponseTime = responseTimes.length > 0
                ? round((responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length) / 60, 2) // in minutes
                : null;

            const score = (handledCount >= MIN_HANDLED && avgResponseTime !== null)
                ? handledCount * 2 - avgResponseTime
                : null;

            return {
                consultantName: consultant.name || "N/A",
                id: consultant.id,
                inquiriesHandled: handledCount,
                avgResponseTime: avgResponseTime,
                score,
                avatar: consultant.avatar || undefined,
            };
        });

        const valid = consultantStats.filter(s => s.score !== null);
        const sorted = valid.sort((a, b) => b.score - a.score);
        setLeaderboard(sorted.slice(0, 3));
    }, [inquiries, allConsultants, firestoreInquiriesLoaded]); // Depends on the memoized 'allConsultants'

    return (
        <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Top Responders <Speed sx={{ verticalAlign: 'middle', color: 'primary.main', ml: 0.5 }} />
            </Typography>
            <List sx={{ flexGrow: 1 }}>
                {leaderboard.length === 0 ? (
                    <ListItem>
                        <ListItemText primary="No qualifying responders yet." secondary={`Minimum ${MIN_HANDLED} inquiries required.`} />
                    </ListItem>
                ) : (
                    leaderboard.map((responder, idx) => (
                        <ListItem key={responder.id} sx={{ bgcolor: idx === 0 ? 'primary.light' : 'inherit', borderRadius: 1, mb: 1, p: 1.5 }}>
                            <ListItemAvatar>
                                <Avatar src={responder.avatar} sx={{ bgcolor: idx === 0 ? 'primary.dark' : 'secondary.main' }}>
                                    {responder.consultantName[0] || "?"}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={`${idx + 1}. ${responder.consultantName}`}
                                secondary={`Avg. Response: ${responder.avgResponseTime} min | Handled: ${responder.inquiriesHandled}`}
                                primaryTypographyProps={{ fontWeight: 600 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: idx === 0 ? 'primary.main' : 'text.primary' }}>
                                {round(responder.score)}
                            </Typography>
                        </ListItem>
                    ))
                )}
            </List>
            <Typography variant="caption" sx={{ mt: 'auto', pt: 1, display: 'block', color: 'text.secondary', textAlign: 'center' }}>
                *Score is based on inquiries handled and response speed.
            </Typography>
        </Paper>
    );
}

const TILE_HEIGHT = 180; // A-OK
const TILE_WIDTH = 250; // A-OK

function StatCard({ label, value, unit, icon, color, isLoading }) { // A-OK
  return (
    <Paper
      elevation={3}
      sx={{
        height: `${TILE_HEIGHT}px`,
        width: { xs: '100%', sm: `${TILE_WIDTH}px` },
        minHeight: `${TILE_HEIGHT}px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        textAlign: "center"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
              {isLoading ? "..." : value}
              {unit && (
              <Typography component="span" variant="h5" sx={{ ml: 0.5, fontWeight: 500, color: 'inherit' }}>
                  {unit}
              </Typography>
              )}
          </Typography>
          <Box sx={{ fontSize: 38, color: color, display: 'flex', alignItems: 'center' }}>
              {icon}
          </Box>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: "text.secondary", mt: 1 }}>
          {label}
      </Typography>
    </Paper>
  );
}

// This dialog correctly fetches *only* consultants for assignment. No change needed.
function AssignInquiryDialog({ open, onClose, onAssign, inquiry }) { // A-OK
    const [consultants, setConsultants] = useState([]);
    const [loadingDialog, setLoadingDialog] = useState(true);
    const [selectedConsultantId, setSelectedConsultantId] = useState(null);

    useEffect(() => {
        if (open) {
            setLoadingDialog(true);
            setSelectedConsultantId(null);

            const fetchConsultants = async () => {
                try {
                    // This endpoint is correct, we only want to assign to consultants
                    const response = await api.get("/admin/consultants");
                    const fetchedConsultants = Array.isArray(response.data) ? response.data : [];
                    setConsultants(fetchedConsultants);

                    if (inquiry?.consultantId && fetchedConsultants.length > 0) {
                        const currentAssigned = fetchedConsultants.find(
                            c => String(c.id) === String(inquiry.consultantId) || c.email === inquiry.consultantId
                        );
                        if (currentAssigned) {
                            setSelectedConsultantId(currentAssigned.id);
                        }
                    }
                } catch (error) {
                    console.error("AssignInquiryDialog: Error fetching consultants:", error);
                    setConsultants([]);
                } finally {
                    setLoadingDialog(false);
                }
            };
            fetchConsultants();
        } else {
            setConsultants([]);
            setSelectedConsultantId(null);
            setLoadingDialog(true);
        }
    }, [open, inquiry?.consultantId]);

    const handleAssign = () => {
        if (selectedConsultantId) {
            onAssign(selectedConsultantId);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                Assign Inquiry
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {inquiry?.formData?.fullName && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 2, pb: 1, fontWeight: 500 }}>
                        To: {inquiry.formData.fullName} (ID: {inquiry?.id?.substring(0, 6)}...)
                    </Typography>
                )}
                {loadingDialog ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : consultants.length === 0 ? (
                    <Typography sx={{ p: 3, textAlign: "center", fontStyle: 'italic', color: 'text.secondary' }}>
                        No consultants available to assign.
                    </Typography>
                ) : (
                    <List dense sx={{ maxHeight: 350, overflow: 'auto', py:1 }}>
                        {consultants.map((consultant) => (
                            <ListItemButton
                                key={consultant.id}
                                selected={selectedConsultantId === consultant.id}
                                onClick={() => setSelectedConsultantId(consultant.id)}
                                sx={{
                                    mx: 1.5, my: 0.5, borderRadius: '8px',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.light',
                                        color: 'primary.contrastText',
                                        '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary': { color: 'primary.contrastText' }
                                    },
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: selectedConsultantId === consultant.id ? 'primary.dark' : 'secondary.main', color: 'secondary.contrastText' }}>
                                        {consultant.name ? consultant.name.charAt(0).toUpperCase() : "?"}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={consultant.name || "Unnamed Consultant"}
                                    secondary={`Email: ${consultant.email || "N/A"}`}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px', borderTop: 1, borderColor: 'divider' }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleAssign}
                    color="primary"
                    variant="contained"
                    disabled={!selectedConsultantId || loadingDialog}
                    sx={{ borderRadius: '8px' }}
                >
                    Assign Consultant
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// This component now receives a filtered list of *only* consultants
function AdminInquiryChatView({ inquiry, onBack, consultants, currentAppId, adminUser, onAdminMessageSent }) { // A-OK
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [messageFetchError, setMessageFetchError] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);
    const isServiceProvider = inquiry?.inquiryUserType === 'service_provider_v2';
    const claimedOwnerId = String(inquiry?.consultantId || '').trim();
    const claimedOwnerName = String(inquiry?.consultantName || '').trim();
    const isClaimedInquiry = claimedOwnerId !== '' || claimedOwnerName !== '';
    const claimedOwnerLabel = claimedOwnerName || (claimedOwnerId !== '' ? `Consultant ${claimedOwnerId}` : 'another consultant');
    const isReadOnly = isClaimedInquiry;

    useEffect(() => {
        setMessageFetchError(null);
        if (!inquiry || !firestoreDb || !currentAppId) {
            setLoadingMessages(false);
            setMessages([]);
            setMessageFetchError("Required inquiry data or system config is missing to fetch messages.");
            return;
        }
        setLoadingMessages(true);

        const appIdToUse = inquiry.appId || currentAppId;
        const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
        const messagesPath = `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}/messages`;

        const q = query(collection(firestoreDb, messagesPath), orderBy("timestamp", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoadingMessages(false);
        }, (error) => {
            console.error(`Error fetching messages from ${messagesPath}:`, error);
            setMessageFetchError(`Failed to load messages: ${error.message}.`);
            setLoadingMessages(false);
        });

        return () => unsubscribe();
    }, [inquiry, currentAppId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !isServiceProvider || isSending || isReadOnly) return;
        setIsSending(true);

        const appIdToUse = inquiry.appId || currentAppId;
        const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
        const messagesPath = `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}/messages`;

        try {
            await addDoc(collection(firestoreDb, messagesPath), {
                text: newMessage.trim(),
                sender: 'admin',
                senderId: adminUser.uid,
                senderName: adminUser.displayName || adminUser.email || 'Admin',
                timestamp: serverTimestamp(),
            });

            await updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
                lastMessageSnippet: `Admin: ${newMessage.trim().substring(0, 40)}`,
                lastMessageBy: 'admin',
                updatedAt: serverTimestamp(),
            });
            setNewMessage("");
            onAdminMessageSent();
        } catch (error) {
            console.error("Admin Send Message Error:", error);
            setMessageFetchError("Failed to send message. Please try again.");
        } finally {
            setIsSending(false);
        }
    };


    const getSenderDetails = (msg) => {
        if (msg.senderType === 'user' || msg.sender === inquiry.userId) {
            return { name: 'Customer', align: 'flex-start', bg: 'grey.200', color: 'text.primary', isSystem: false };
        }
        if (msg.sender === 'consultant') {
            // 'consultants' prop is now pre-filtered
            const consultant = consultants.find(c => String(c.id) === String(msg.senderId));
            return { name: msg.senderName || consultant?.name || 'Consultant', align: 'flex-end', bg: 'primary.main', color: 'primary.contrastText', isSystem: false };
        }
        if (msg.sender === 'admin') {
            return { name: msg.senderName || 'Admin', align: 'flex-end', bg: '#673ab7', color: 'white', isSystem: false };
        }
        if (msg.sender === 'system') {
            return { name: 'System', align: 'center', bg: 'transparent', color: 'text.secondary', isSystem: true };
        }
        return { name: 'Unknown', align: 'center', bg: 'grey.300', color: 'text.secondary', isSystem: true };
    };


    if (!inquiry) return <Paper sx={{p:3, borderRadius: '12px'}}><Typography>No inquiry selected.</Typography></Paper>;

    return (
        <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', borderRadius: '16px', overflow:'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Button onClick={onBack} startIcon={<ArrowBackIcon />} variant="outlined" size="small" sx={{borderRadius: '8px'}}>Back</Button>
                <Box textAlign="center">
                    <Typography variant="h6" component="div">{inquiry.formData?.fullName || "N/A"}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                        <Chip label={inquiry.status?.replace(/_/g, ' ') || "N/A"} size="small" color={inquiry.status?.includes('closed') ? 'success' : 'warning'} />
                        {isServiceProvider && <Chip label="Provider" size="small" color="secondary" variant="outlined" />}
                        {isReadOnly && <Chip label={`Handled by ${claimedOwnerLabel}`} size="small" color="info" variant="outlined" />}
                    </Box>
                </Box>
                <Box sx={{ width: 80 }} />
            </Box>

            {isReadOnly && (
                <Alert severity="info" sx={{ m: 2, mb: 0, borderRadius: '10px' }}>
                    Read-only view. This inquiry is currently being handled by <strong>{claimedOwnerLabel}</strong>. Admin can monitor the conversation here without taking ownership.
                </Alert>
            )}

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p:2, background: (theme) => theme.palette.background.default }}>
                {loadingMessages ? <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height:'100%'}}><CircularProgress /></Box> :
                    messageFetchError ? <Alert severity="error" sx={{m:2, borderRadius: '8px'}}>{messageFetchError}</Alert> :
                    messages.length === 0 ? <Typography sx={{textAlign:'center', p:3, fontStyle:'italic', color:'text.secondary'}}>No messages found.</Typography> :
                    messages.map((msg) => {
                        const { name, align, bg, color, isSystem } = getSenderDetails(msg);
                        if (isSystem) {
                            return (
                                <Typography key={msg.id} variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', my: 1, display: 'block', fontStyle: 'italic' }}>
                                    {msg.text}
                                </Typography>
                            );
                        }
                        return (
                            <Box key={msg.id} sx={{ display: 'flex', justifyContent: align, mb: 1.5 }}>
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                                    <Box sx={{ p: 1.5, borderRadius: align === 'flex-start' ? '12px 12px 12px 0' : '12px 12px 0 12px', bgcolor: bg, color: color, maxWidth: '75%', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{msg.text}</Typography>
                                        <Typography variant="caption" component="div" sx={{ textAlign: 'right', fontSize: '0.7rem', opacity:0.8, mt:0.5 }}>
                                            {name} - {msg.timestamp ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ''}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Box>
                        )
                    })}
                <div ref={messagesEndRef} />
            </Box>

            <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider', background: (theme) => theme.palette.background.paper, display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={
                        isReadOnly
                            ? `Read-only. ${claimedOwnerLabel} is actively handling this inquiry.`
                            : isServiceProvider
                                ? "Send a message as Admin..."
                                : "Admin messaging is for Service Providers only."
                    }
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={isReadOnly || !isServiceProvider || isSending}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                />
                <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={isReadOnly || !isServiceProvider || isSending || !newMessage.trim()}
                    sx={{ borderRadius: '8px' }}
                >
                    {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                </Button>
            </Box>
        </Paper>
    );
}

export default function AdminDashboard() {
    const { logout, user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
    const [drawerOpen, setDrawerOpen] = useState(isDesktop);
    const [isMiniDrawer, setIsMiniDrawer] = useState(false);

    const [selectedMenu, setSelectedMenu] = useState("Dashboard");
    const [loading, setLoading] = useState(true);
    const [firestoreInquiriesLoaded, setFirestoreInquiriesLoaded] = useState(false);

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedInquiryForDialog, setSelectedInquiryForDialog] = useState(null);

    const [consultantStats, setConsultantStats] = useState([]);
    const [whatsAppUsage, setWhatsAppUsage] = useState(null);
    const [whatsAppUsageLoading, setWhatsAppUsageLoading] = useState(true);
    const [whatsAppUsageError, setWhatsAppUsageError] = useState("");

    // Global broadcast budget (admin adjuster)
    const [broadcastBudget, setBroadcastBudget] = useState({ email: null, whatsapp: null });
    const [broadcastBudgetLoading, setBroadcastBudgetLoading] = useState(true);

    // --- STATE RENAMES & ADDITIONS ---
    const [allUsers, setAllUsers] = useState([]); // Renamed from allConsultants
    const [usersLoading, setUsersLoading] = useState(true); // Renamed from consultantsLoading
    const [newUserName, setNewUserName] = useState(""); // Renamed from newConsultantName
    const [newUserEmail, setNewUserEmail] = useState(""); // Renamed from newConsultantEmail
    const [newUserRole, setNewUserRole] = useState("Consultant"); // *** NEW STATE FOR ROLE ***
    const [newUserWhatsapp, setNewUserWhatsapp] = useState("");
    const [newUserMailboxEmail, setNewUserMailboxEmail] = useState("");
    // --- END STATE RENAMES ---

    const [inquiries, setInquiries] = useState([]);
    const [viewingInquiryDetail, setViewingInquiryDetail] = useState(null);

    const [selectedConsultantForTask, setSelectedConsultantForTask] = useState('');
    const [taskDescription, setTaskDescription] = useState('');
    const [assigningTask, setAssigningTask] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');

    const [isCreating, setIsCreating] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);

    // --- STATE RENAMES FOR DIALOGS ---
    const [userToReset, setUserToReset] = useState(null); // Renamed from consultantToReset
    const [isResetting, setIsResetting] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null); // Renamed from editingConsultant
    // Updated edit form to include role
    const [editFormData, setEditFormData] = useState({ name: '', email: '', password: '', role: '', mailbox_email: '' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null); // Renamed from deletingConsultant
    // --- END STATE RENAMES ---
    const inquiryDeepLinkHandledRef = useRef('');


    const theme = useMemo(() => createTheme({ // A-OK
        palette: {
            mode: 'light',
            primary: { main: "#2db838", light: "#66bb6a", dark: "#0e6346", contrastText: "#ffffff" },
            secondary: { main: "#64748b", light: "#94a3b8", dark: "#475569", contrastText: "#ffffff" },
            background: { default: "#f4f6f8", paper: "#ffffff" },
            text: { primary: "#212121", secondary: "#757575" },
            divider: 'rgba(0, 0, 0, 0.12)',
            action: { hover: 'rgba(45, 184, 56, 0.06)', selected: 'rgba(45, 184, 56, 0.12)'},
            success: { main: "#4caf50", dark: '#388e3c' }, warning: { main: "#ff9800" }, error: { main: "#f44336" }, info: { main: "#2196f3" }
        },
        typography: {
            fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            h1: { fontWeight: 700, fontSize: '2.8rem' },
            h2: { fontWeight: 700, fontSize: '2.2rem', lineHeight: 1.2 },
            h3: { fontWeight: 700, fontSize: '2rem' },
            h4: { fontWeight: 600, fontSize: '1.6rem' },
            h5: { fontWeight: 600, fontSize: '1.2rem' },
            h6: { fontWeight: 600, fontSize: '1.0rem' },
            button: { textTransform: 'none', fontWeight: 600, borderRadius: '8px' },
            ...appTypography,
        },
        shape: { borderRadius: 16, },
        components: {
            MuiPaper: { defaultProps: { elevation: 0, }, styleOverrides: { root: { border: '1px solid rgba(0,0,0,0.08)', }}},
            MuiAppBar: { styleOverrides: { root: { boxShadow: 'none', backgroundColor: '#ffffff', borderBottom: '1px solid rgba(0,0,0,0.12)', color: '#212121' }}},
            MuiDrawer: { styleOverrides: { paper: { borderRight: 'none', boxShadow: '2px 0 10px -2px rgba(0,0,0,0.05)', backgroundColor: '#ffffff', }}},
            MuiButton: { styleOverrides: {
                containedPrimary: { boxShadow: '0 4px 12px -4px rgba(45, 184, 56, 0.5)', '&:hover': { backgroundColor: "#0e6346", boxShadow: '0 6px 16px -4px rgba(45, 184, 56, 0.6)',}},
                outlinedPrimary: { borderColor: "rgba(45, 184, 56, 0.5)", color: "#2db838", '&:hover': { borderColor: "#2db838", backgroundColor: 'rgba(45, 184, 56, 0.04)',}}
            }},
            MuiIconButton: { styleOverrides: { root: { color: "#2db838", '&:hover': { backgroundColor: 'rgba(45, 184, 56, 0.08)'}}}},
            MuiCssBaseline: { styleOverrides: (themeParam) => `
                body { scrollbar-width: thin; scrollbar-color: ${themeParam.palette.primary.dark} ${themeParam.palette.background.default}; }
                body::-webkit-scrollbar { width: 8px; } body::-webkit-scrollbar-track { background: ${themeParam.palette.background.default}; }
                body::-webkit-scrollbar-thumb { background-color: ${themeParam.palette.primary.dark}; border-radius: 10px; border: 2px solid ${themeParam.palette.background.default}; }
            `,},
        }
    }), []);

    useEffect(() => { // A-OK
        if (isDesktop) {
            setDrawerOpen(true);
        } else {
            setDrawerOpen(false);
            setIsMiniDrawer(false);
        }
    }, [isDesktop]);

    const handleDrawerToggle = () => { // A-OK
        if (isDesktop) {
            setIsMiniDrawer(!isMiniDrawer);
        } else {
            setDrawerOpen(!drawerOpen);
        }
    };

    const showSnackbar = useCallback((message, severity = 'info') => { // A-OK
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    const handleApiError = useCallback((err, defaultMessage) => { // A-OK
        const message = err.response?.data?.message || err.message || defaultMessage;
        showSnackbar(message, "error");
        console.error(defaultMessage, err);
    }, [showSnackbar]);

    // Renamed fetchConsultants to fetchUsers
    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            // Updated API endpoint
            const response = await api.get("/admin/users");
            setAllUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            handleApiError(error, "Failed to load user list");
        } finally {
            setUsersLoading(false);
        }
    }, [handleApiError]);

    const fetchWhatsAppUsage = useCallback(async () => {
        setWhatsAppUsageLoading(true);
        setWhatsAppUsageError("");

        try {
            const response = await api.get("/admin/whatsapp-usage");
            setWhatsAppUsage(response?.data?.data ?? null);
        } catch (error) {
            console.error("Failed to load WhatsApp usage monitor", error);
            setWhatsAppUsage(null);
            setWhatsAppUsageError(error.response?.data?.message || error.message || "Failed to load WhatsApp usage monitor");
        } finally {
            setWhatsAppUsageLoading(false);
        }
    }, []);

    const fetchBroadcastBudget = useCallback(async () => {
        setBroadcastBudgetLoading(true);
        try {
            const res = await api.get("/analytics/marketing/broadcast-budget");
            const channels = res.data?.data?.channels || res.data?.channels || {};
            setBroadcastBudget({
                email: channels.email || null,
                whatsapp: channels.whatsapp || null,
            });
        } catch (error) {
            console.warn("Failed to load broadcast budget", error);
            setBroadcastBudget({ email: null, whatsapp: null });
        } finally {
            setBroadcastBudgetLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchUsers(); // Renamed function call
            fetchWhatsAppUsage();
            fetchBroadcastBudget();
        }
    }, [user, fetchUsers, fetchWhatsAppUsage, fetchBroadcastBudget]); // Updated dependency

    useEffect(() => {
        if (!user) return undefined;

        const intervalId = window.setInterval(() => {
            fetchWhatsAppUsage();
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, [user, fetchWhatsAppUsage]);

    useEffect(() => { // A-OK
        setLoading(true);
        if (!firestoreDb) {
            console.error("Firestore DB not available");
            setLoading(false);
            return;
        }

        const q = query(collection(firestoreDb, "allInquiries"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updatedInquiries = snapshot.docs.map(docSnapshot => ({ id: docSnapshot.id, ...docSnapshot.data() }));
            setInquiries(updatedInquiries);
            setFirestoreInquiriesLoaded(true);
            setLoading(false);
        }, (error) => {
            console.error("Firestore Error loading inquiries:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const { metrics, overdueInquiries, customerDatabase, derivedReminderCount } = useMemo(() => { // A-OK
        if (!firestoreInquiriesLoaded) {
            return { metrics: { total: 0, unanswered: 0, rate: 0 }, overdueInquiries: [], customerDatabase: [], derivedReminderCount: 0 };
        }

        const now = new Date();
        const closedStatuses = ['closed_by_consultant', 'closed_by_user','closed_by_provider'];
        const answeredStatuses = ['closed_by_consultant', 'answered', 'closed_by_user', 'closed_by_provider','pending_user_response', 'active_chat'];

        let unansweredCount = 0;
        const overdue = [];
        const reminders = [];
        const customerMap = new Map();

        inquiries.forEach(inq => {
            if (!inq.status || !answeredStatuses.some(s => inq.status.includes(s))) {
                unansweredCount++;
            }

            const isClosed = closedStatuses.includes(inq.status);
            const createdAt = inq.createdAt?.toDate ? inq.createdAt.toDate() : null;
            if (createdAt && !isClosed) {
                const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
                if (hoursDiff > 48) {
                    overdue.push(inq);
                }
            }

            if (inq.reminder_24h_sent && !isClosed) {
                reminders.push(inq);
            }

            const customerKey = inq.userId || inq.formData?.email;
            if (customerKey && inq.formData) {
                if (!customerMap.has(customerKey)) {
                    customerMap.set(customerKey, {
                        id: customerKey,
                        fullName: inq.formData.fullName,
                        email: inq.formData.email,
                        phone: inq.formData.phone,
                        inquiryTypes: new Set(),
                        lastInteraction: new Date(0),
                        isServiceProvider: inq.inquiryUserType === 'service_provider_v2',
                        inquiryCount: 0
                    });
                }
                const customerEntry = customerMap.get(customerKey);
                customerEntry.inquiryCount++;
                if (inq.type) customerEntry.inquiryTypes.add(inq.type);
                const inquiryDate = inq.createdAt?.toDate ? inq.createdAt.toDate() : new Date(0);
                if (inquiryDate > customerEntry.lastInteraction) {
                    customerEntry.lastInteraction = inquiryDate;
                }
            }
        });

        const total = inquiries.length;
        const answered = total - unansweredCount;
        const rate = total > 0 ? round((answered / total) * 100, 1) : 0;

        const derivedCustomers = Array.from(customerMap.values()).map(c => ({...c, inquiryTypes: Array.from(c.inquiryTypes)}));
        derivedCustomers.sort((a,b) => b.lastInteraction - a.lastInteraction);

        return {
            metrics: { total, unanswered: unansweredCount, rate },
            overdueInquiries: overdue,
            customerDatabase: derivedCustomers,
            derivedReminderCount: reminders.length
        };

    }, [inquiries, firestoreInquiriesLoaded]);

    // This effect now filters allUsers to get consultants for stats
    useEffect(() => {
        const allConsultants = allUsers.filter(u => u.role === 'Consultant');

        if (!firestoreInquiriesLoaded || allConsultants.length === 0) {
            setConsultantStats([]);
            return;
        }

        const newConsultantStats = allConsultants.map(consultant => {
            const assignedInquiries = inquiries.filter(inq => String(inq.consultantId) === String(consultant.id));
            const handledCount = assignedInquiries.filter(inq => inq.status && !inq.status.includes('new')).length;

            const responseTimes = assignedInquiries
                .map(inq => {
                    const createdAt = inq.createdAt?.toDate ? inq.createdAt.toDate() : null;
                    const openedAt = inq.consultantOpenedAt?.toDate ? inq.consultantOpenedAt.toDate() : null;
                    if (createdAt && openedAt && openedAt > createdAt) {
                        return (openedAt.getTime() - createdAt.getTime()) / 1000;
                    }
                    return null;
                })
                .filter(time => time !== null);

            const avgResponseTime = responseTimes.length > 0
                ? round((responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length) / 60, 2)
                : null;

            return {
                consultantName: consultant.name || "N/A",
                id: consultant.id,
                inquiriesHandled: handledCount,
                avgResponseTime: avgResponseTime,
            };
        });
        setConsultantStats(newConsultantStats);
    }, [inquiries, allUsers, firestoreInquiriesLoaded]); // Depends on allUsers now

    const whatsAppUsageSummary = useMemo(() => {
        const dailyLimit = Number(whatsAppUsage?.daily_api_call_limit ?? 200);
        const outboundSendsToday = Number(whatsAppUsage?.outbound_message_sends_today ?? 0);
        const deliveredCallbacksToday = Number(whatsAppUsage?.delivered_callbacks_today ?? 0);
        const readCallbacksToday = Number(whatsAppUsage?.read_callbacks_today ?? 0);
        const statusCallbacksToday = Number(whatsAppUsage?.status_callbacks_today ?? (deliveredCallbacksToday + readCallbacksToday));
        const remainingOutboundCallsToday = Number(
            whatsAppUsage?.remaining_outbound_calls_today ?? Math.max(0, dailyLimit - outboundSendsToday)
        );
        const outboundUsagePercent = Number(
            whatsAppUsage?.outbound_usage_percent ?? (dailyLimit > 0 ? round((outboundSendsToday / dailyLimit) * 100, 1) : 0)
        );

        let headroomColor = theme.palette.success.main;
        if (outboundUsagePercent >= 85) {
            headroomColor = theme.palette.error.main;
        } else if (outboundUsagePercent >= 60) {
            headroomColor = theme.palette.warning.main;
        }

        return {
            dailyLimit,
            outboundSendsToday,
            deliveredCallbacksToday,
            readCallbacksToday,
            statusCallbacksToday,
            remainingOutboundCallsToday,
            outboundUsagePercent,
            headroomColor,
            timezone: whatsAppUsage?.timezone || 'Africa/Harare',
            date: whatsAppUsage?.window?.date || '',
            notes: Array.isArray(whatsAppUsage?.notes) ? whatsAppUsage.notes : [],
        };
    }, [theme.palette.error.main, theme.palette.success.main, theme.palette.warning.main, whatsAppUsage]);

    const handleOpenAssignDialog = (inquiry) => { setSelectedInquiryForDialog(inquiry); setAssignDialogOpen(true); }; // A-OK

    const handleAssignInquiry = async (selectedConsultantSQLId) => { // A-OK
        if (!selectedInquiryForDialog || !selectedConsultantSQLId) {
            showSnackbar("Please select an inquiry and a consultant.", "error");
            return;
        }
        try {
            // Find consultant from allUsers list
            const assignedConsultantDetails = allUsers.find(c => String(c.id) === String(selectedConsultantSQLId) && c.role === 'Consultant');
            const inquiryDocRef = doc(firestoreDb, "allInquiries", selectedInquiryForDialog.id);
            await updateDoc(inquiryDocRef, {
                consultantId: String(selectedConsultantSQLId),
                consultantName: assignedConsultantDetails?.name || "Assigned",
                status: "assigned",
                assignedBy: "admin",
                updatedAt: serverTimestamp()
            });
            setAssignDialogOpen(false);
            showSnackbar(`Inquiry assigned to ${assignedConsultantDetails?.name}!`, "success");
        } catch (error) {
            showSnackbar(`Failed to assign inquiry: ${error.message}`, "error");
        }
    };

    // This function now filters allUsers to find the target consultant
    const handleAssignTask = async () => {
        if (!selectedConsultantForTask || !taskDescription.trim()) {
            showSnackbar("Please select a consultant and enter a task description.", "warning");
            return;
        }
        setAssigningTask(true);
        const targetConsultant = allUsers.find(c => String(c.id) === String(selectedConsultantForTask) && c.role === 'Consultant');
        try {
            await addDoc(collection(firestoreDb, "consultantTasks"), {
                consultantId: String(selectedConsultantForTask),
                consultantName: targetConsultant?.name || "Unknown",
                taskDescription: taskDescription.trim(),
                assignedBy: "admin",
                assignedAt: serverTimestamp(),
                status: "new",
            });
            showSnackbar(`Task assigned to ${targetConsultant?.name}!`, "success");
            setTaskDescription('');
            setSelectedConsultantForTask('');
        } catch (error) {
            showSnackbar("Failed to assign task: " + error.message, "error");
        } finally {
            setAssigningTask(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    // *** UPDATED MENUS ***
    const menus = [
        { label: "Dashboard", icon: DashboardIcon },
        { label: "Inquiries", icon: MailIcon },
        { label: "Phone Rotations", icon: InboxIcon, action: "open-phone-rotations" },
        { label: "WhatsApp Lines", icon: InboxIcon },
        { label: "Customers", icon: PeopleIcon },
        { label: "Performance", icon: QueryStatsIcon },
        { label: "Broadcast Budget", icon: CampaignIcon },
        { label: `Reminders`, count: derivedReminderCount, icon: NotificationsIcon },
        { label: `Overdue`, count: overdueInquiries.length, icon: WarningIcon },
        { label: "User Management", icon: ManageAccountsIcon },
        { label: "Staff Directory", icon: BadgeIcon },
        { label: "Email Monitoring", icon: CampaignIcon },
        { label: "Email Signature", icon: BadgeIcon },
        { label: "Consultants", icon: AddTaskIcon }, // *** NEW MENU ITEM ***
    ];

    const handleInquiryRowClick = (inquiry) => { setSelectedMenu("Inquiries"); setViewingInquiryDetail(inquiry); }; // A-OK

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openInquiry = params.get("openInquiry");

        if (!openInquiry || !firestoreInquiriesLoaded) {
            return;
        }

        if (inquiryDeepLinkHandledRef.current === openInquiry) {
            return;
        }

        const targetInquiry = inquiries.find((inq) => {
            const candidates = [
                inq.id,
                inq.originalInquiryId,
                inq.sqlId,
                inq.firestore_document_id,
            ].filter(Boolean).map(String);

            return candidates.includes(String(openInquiry));
        });

        if (!targetInquiry) {
            return;
        }

        inquiryDeepLinkHandledRef.current = String(openInquiry);
        setSelectedMenu("Inquiries");
        setViewingInquiryDetail(targetInquiry);
        navigate("/admin", { replace: true });
    }, [firestoreInquiriesLoaded, inquiries, location.search, navigate]);

    // Renamed to handleCreateUser
    const handleCreateUser = async (e) => {
        e.preventDefault();
        // Added role validation
        if (!newUserName || !newUserEmail || !newUserRole) {
            showSnackbar("Please provide a name, email, and role.", "warning");
            return;
        }
        setIsCreating(true);
        try {
            // Updated endpoint and payload
            const response = await api.post("/admin/users", {
                name: newUserName,
                email: newUserEmail,
                role: newUserRole,
                whatsapp_number: newUserWhatsapp || null,
                mailbox_email: newUserMailboxEmail || null,
            });
            showSnackbar(response.data.message || "User created successfully!", "success");
            setNewUserName("");
            setNewUserEmail("");
            setNewUserRole("Consultant");
            setNewUserWhatsapp("");
            setNewUserMailboxEmail("");
            fetchUsers(); // Refresh user list
        } catch (err) {
            handleApiError(err, 'Failed to create user');
        } finally {
            setIsCreating(false);
        }
    };

    // Renamed functions to be generic
    const openResetUserDialog = (user) => {
        setUserToReset(user);
        setResetDialogOpen(true);
    };
    const closeResetDialog = () => {
        setUserToReset(null);
        setResetDialogOpen(false);
    };
    const handleResetUserPassword = async () => {
        if (!userToReset) return;
        setIsResetting(true);
        try {
            // Updated endpoint
            const response = await api.post(`/admin/users/${userToReset.id}/reset-password`);
            showSnackbar(response.data.message || "Password reset successfully.", "success");
            closeResetDialog();
        } catch (err) {
            handleApiError(err, 'An error occurred during password reset.');
        } finally {
            setIsResetting(false);
        }
    };

    const handleEditUserOpen = (user) => {
        setEditingUser(user);
        setEditFormData({
            name: user.name,
            email: user.email,
            password: '',
            role: user.role || 'Consultant',
            whatsapp_number: user.whatsapp_number || '',
            mailbox_email: user.mailbox_email || '',
        });
        setEditModalOpen(true);
    };
    const handleEditUserClose = () => {
        setEditModalOpen(false);
        setEditingUser(null);
    };
    const handleEditUserSave = async () => {
        if (!editingUser) return;
        const updatePayload = {
            name: editFormData.name,
            email: editFormData.email,
            role: editFormData.role,
            whatsapp_number: editFormData.whatsapp_number || null,
            mailbox_email: editFormData.mailbox_email || null,
        };
        if (editFormData.password) {
            updatePayload.password = editFormData.password;
        }
        try {
            // Updated endpoint
            await api.put(`/admin/users/${editingUser.id}`, updatePayload);
            showSnackbar('User updated successfully!', 'success');
            handleEditUserClose();
            fetchUsers(); // Refresh user list
        } catch (err) {
            handleApiError(err, 'Failed to update user');
        }
    };

    const handleDeleteUserOpen = (user) => {
        setDeletingUser(user);
        setDeleteModalOpen(true);
    };
    const handleDeleteUserClose = () => {
        setDeleteModalOpen(false);
        setDeletingUser(null);
    };
    const handleDeleteUserConfirm = async () => {
        if (!deletingUser) return;
        try {
            // Updated endpoint
            await api.delete(`/admin/users/${deletingUser.id}`);
            showSnackbar('User deleted successfully!', 'success');
            handleDeleteUserClose();
            fetchUsers(); // Refresh user list
        } catch (err) {
            handleApiError(err, 'Failed to delete user');
            handleDeleteUserClose();
        }
    };


    // This function now filters allUsers to find the target consultant
    const handleSendReminder = async (inquiry) => {
        if (!inquiry.consultantId) {
            showSnackbar("Cannot send reminder: Inquiry is not assigned to a consultant.", "warning");
            return;
        }

        // Find consultant from allUsers list
        const targetConsultant = allUsers.find(c => String(c.id) === String(inquiry.consultantId) && c.role === 'Consultant');

        try {
            // Create a new document in the 'consultantTasks' collection in Firestore
            await addDoc(collection(firestoreDb, "consultantTasks"), {
                consultantId: String(inquiry.consultantId),
                consultantName: targetConsultant?.name || "Unknown Consultant",
                inquiryId: inquiry.id,
                taskType: "REMINDER",
                message: `Please follow up on the inquiry from: ${inquiry.formData?.fullName || 'N/A'}.`,
                isRead: false,
                createdAt: serverTimestamp(),
            });

            showSnackbar(`Reminder sent to ${targetConsultant?.name}!`, "success");

        } catch (err) {
            console.error("Failed to send reminder via Firestore:", err);
            handleApiError(err, "Failed to send reminder");
        }
    };

    const renderAnalytics = () => { // A-OK (relies on consultantStats, which is now correctly filtered)
        const CHART_HEIGHT = 600;
        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>Consultant Performance</Typography>
                {loading ? <Box sx={{display:'flex', justifyContent:'center', my:5}}><CircularProgress size={50} /></Box> :
                <Grid container spacing={GUTTER}>
                    <Grid item xs={12} sx={{ mb: GUTTER }}>
                        <Paper sx={{ p: {xs: 1, sm: 2, md: 3}, height: CHART_HEIGHT, borderRadius: '20px' }}>
                            <Typography variant="h5" gutterBottom sx={{fontWeight:600}}>Inquiries Handled</Typography>
                            <ResponsiveContainer width="101%" height="90%">
                                <BarChart data={consultantStats} margin={{ top: 20, right: 30, left: 0, bottom: 65 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="consultantName" tick={{ fontSize: 12 }} angle={-35} textAnchor="end" interval={0} height={70} />
                                    <YAxis allowDecimals={false} />
                                    <RechartsTooltip />
                                    <RechartsLegend />
                                    <Bar dataKey="inquiriesHandled" fill={theme.palette.primary.main} name="Inquiries Handled" barSize={35} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: {xs: 1, sm: 2, md: 3}, height: CHART_HEIGHT, borderRadius: '20px' }}>
                            <Typography variant="h5" gutterBottom sx={{fontWeight:600}}>Avg. Response Time (min)</Typography>
                            <ResponsiveContainer width="101%" height="90%">
                                <BarChart data={consultantStats.filter(s => s.avgResponseTime !== null)} margin={{ top: 20, right: 30, left: 0, bottom: 65 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="consultantName" tick={{ fontSize: 12 }} angle={-35} textAnchor="end" interval={0} height={70}/>
                                    <YAxis />
                                    <RechartsTooltip />
                                    <RechartsLegend />
                                    <Bar dataKey="avgResponseTime" fill={theme.palette.primary.dark} name="Avg. Response Time (min)" barSize={35} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                </Grid>
                }
            </motion.div>
        );
    };

    const renderInquiriesTable = (filteredInquiries, title) => ( // A-OK
        <Paper sx={{ p: {xs:1.5, sm:2}, overflowX: "auto", borderRadius: '16px' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 700 }}>{title} ({filteredInquiries.length})</Typography>
            {loading ? <Box sx={{display:'flex', justifyContent:'center', p:3 }}><CircularProgress /></Box> :
            !filteredInquiries.length ? <Typography sx={{textAlign:'center', p:2, fontStyle:'italic'}}>No inquiries found.</Typography> :
            <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table size="medium" stickyHeader sx={{ minWidth: 480 }}>
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'background.paper' } }}>
                            <TableCell>Customer</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>Created</TableCell>
                            <TableCell sx={{display: {xs: 'none', lg: 'table-cell'}}}>Consultant</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInquiries.map((inq) => {
                            const isServiceProvider = inq.inquiryUserType === 'service_provider_v2';
                            return (
                                <MotionTableRow
                                    key={inq.id}
                                    hover
                                    onClick={() => handleInquiryRowClick(inq)}
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                    sx={{cursor: 'pointer'}}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 500 }}>{inq.formData?.fullName || "Unknown"}</Typography>
                                            {isServiceProvider && (
                                                <Chip label="Provider" size="small" variant="outlined" sx={{ color: theme.palette.success.dark, borderColor: theme.palette.success.dark, pointerEvents: 'none' }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell><Chip label={inq.status || "Pending"} size="small" variant="outlined" onClick={(e) => e.stopPropagation()} /></TableCell>
                                    <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>{inq.createdAt?.toDate ? inq.createdAt.toDate().toLocaleDateString() : "N/A"}</TableCell>
                                    <TableCell sx={{display: {xs: 'none', lg: 'table-cell'}}}>{inq.consultantName || "Unassigned"}</TableCell>
                                    <TableCell align="right">
                                        <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); handleOpenAssignDialog(inq); }} sx={{ mr: 1 }}>
                                            Assign
                                        </Button>
                                        {/* Reminder Button Firestore function */}
                                        <Tooltip title="Send Reminder to Consultant">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="secondary"
                                                onClick={(e) => { e.stopPropagation(); handleSendReminder(inq); }}
                                                disabled={!inq.consultantId}
                                            >
                                                <CampaignIcon fontSize="small" />
                                            </Button>
                                        </Tooltip>
                                    </TableCell>
                                </MotionTableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            }
        </Paper>
    );

    const renderCustomersView = () => ( // A-OK
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Paper sx={{ p: {xs:1.5, sm:2}, borderRadius: '16px' }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 700 }}>Customer Database ({customerDatabase.length})</Typography>
                {loading ? <Box sx={{display:'flex', justifyContent:'center', p:3}}><CircularProgress /></Box> :
                !customerDatabase.length ? <Typography sx={{textAlign:'center', p:2, fontStyle:'italic'}}>No customers found.</Typography> :
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                <TableCell>Name</TableCell>
                                <TableCell sx={{display: {xs: 'none', sm: 'table-cell'}}}>Contact</TableCell>
                                <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>Inquiry Types</TableCell>
                                <TableCell>Last Interaction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerDatabase.map((cust) => (
                                <TableRow key={cust.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {cust.fullName}
                                            {cust.isServiceProvider && <Chip label="Provider" size="small" variant="outlined" color="secondary" sx={{ pointerEvents: 'none' }}/>}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{display: {xs: 'none', sm: 'table-cell'}}}>{cust.email}<br/>{cust.phone}</TableCell>
                                    <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>{cust.inquiryTypes.join(', ')}</TableCell>
                                    <TableCell>{cust.lastInteraction.toLocaleDateString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                }
            </Paper>
        </motion.div>
    );

    // *** REMOVED "ASSIGN TASK" FROM THIS VIEW ***
    const renderUserManagementView = () => {
        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* --- "ASSIGN TASK" PAPER BLOCK REMOVED --- */}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2.5 }}>
                                Create New User
                            </Typography>
                            <Box component="form" onSubmit={handleCreateUser} noValidate>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    value={newUserName}
                                    onChange={(e) => setNewUserName(e.target.value)}
                                    margin="normal"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={newUserEmail}
                                    onChange={(e) => setNewUserEmail(e.target.value)}
                                    margin="normal"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="WhatsApp Number (e.g. +263771234567)"
                                    type="tel"
                                    value={newUserWhatsapp}
                                    onChange={(e) => setNewUserWhatsapp(e.target.value)}
                                    margin="normal"
                                    helperText="Used for escalation alerts and reminders"
                                    InputProps={{ startAdornment: <span style={{ marginRight: 6, fontSize: 18 }}>💬</span> }}
                                />
                                <TextField
                                    fullWidth
                                    label="Company Mailbox Email"
                                    type="email"
                                    value={newUserMailboxEmail}
                                    onChange={(e) => setNewUserMailboxEmail(e.target.value)}
                                    margin="normal"
                                    helperText="Example: brenda@abisiniya.com"
                                />
                                {/* --- NEW ROLE DROPDOWN --- */}
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={newUserRole}
                                        onChange={(e) => setNewUserRole(e.target.value)}
                                        label="Role"
                                    >
                                        <MenuItem value="Consultant">Consultant</MenuItem>
                                        <MenuItem value="Marketing">Marketing</MenuItem>
                                        <MenuItem value="SuperAdmin">Super Admin</MenuItem>
                                    </Select>
                                </FormControl>
                                {/* --- END NEW ROLE DROPDOWN --- */}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, py: 1.5 }}
                                    disabled={isCreating}
                                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                                >
                                    {isCreating ? "Creating..." : "Create & Send Invite"}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', height: '100%' }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                                Manage Existing Users
                            </Typography>
                            <TableContainer sx={{ maxHeight: 440 }}>
                                 {usersLoading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                        <CircularProgress />
                                    </Box>
                                 ) : (
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                                <TableCell>Name</TableCell>
                                                <TableCell sx={{display: {xs: 'none', sm: 'table-cell'}}}>Email</TableCell>
                                                <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>Mailbox</TableCell>
                                                <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>WhatsApp</TableCell>
                                                <TableCell>Role</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allUsers.map((user) => (
                                                <TableRow key={user.id} hover>
                                                    <TableCell>{user.name}</TableCell>
                                                    <TableCell sx={{display: {xs: 'none', sm: 'table-cell'}}}>{user.email}</TableCell>
                                                    <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>
                                                        {user.mailbox_email
                                                            ? <Chip
                                                                size="small"
                                                                label={user.mailbox_email}
                                                                color={user.mailbox_requires_reauth ? "warning" : user.mailbox_status === "connected" ? "success" : "default"}
                                                                variant={user.mailbox_status === "connected" ? "filled" : "outlined"}
                                                              />
                                                            : <Typography variant="caption" color="text.disabled">Not assigned</Typography>
                                                        }
                                                    </TableCell>
                                                    <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}>
                                                        {user.whatsapp_number
                                                            ? <Chip size="small" label={user.whatsapp_number} icon={<span style={{paddingLeft:6}}>💬</span>} sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: 11 }} />
                                                            : <Typography variant="caption" color="text.disabled">Not set</Typography>
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                    <Chip
                                                    label={user.role || "N/A"}
                                                     size="small"
                                                     variant="outlined"
                                                     color={(() => {
                                                     const r = String(user.role || "").toLowerCase();
                                                     if (r === "consultant") return "primary";
                                                     if (r === "marketing") return "secondary";
                                                     if (r === "superadmin") return "warning";
                                                     if (r === "admin") return "info";
                                                     return "default";
                                                     })()}
                                                     />

                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Reset Password">
                                                            {/* Use renamed function */}
                                                            <IconButton size="small" color="secondary" onClick={() => openResetUserDialog(user)}>
                                                                <LockResetIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit User">
                                                            {/* Use renamed function */}
                                                            <IconButton size="small" color="primary" onClick={() => handleEditUserOpen(user)} sx={{ mx: 0.5 }}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete User">
                                                            {/* Use renamed function */}
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteUserOpen(user)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                 )}
                            </TableContainer>
                            {allUsers.length === 0 && !usersLoading &&
                                <Typography sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                                    No users found. Use the form to add one.
                                </Typography>
                            }
                        </Paper>
                    </Grid>
                </Grid>
            </motion.div>
        );
    }

    // *** NEW FUNCTION FOR THE "CONSULTANTS" PAGE ***
    const renderConsultantsView = () => {
        // Filter for *only* consultants for the task assignment dropdown
        const consultantsForTaskDropdown = allUsers.filter(u => u.role === 'Consultant');

        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: '16px' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2.5 }}>Assign Task to Consultant</Typography>
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} md={5}>
                            <FormControl fullWidth variant="outlined" sx={{ height: '100%' }}>
                                <InputLabel>Select Consultant</InputLabel>
                                <Select
                                    value={selectedConsultantForTask}
                                    onChange={(e) => setSelectedConsultantForTask(e.target.value)}
                                    label="Select Consultant"
                                >
                                    {consultantsForTaskDropdown.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField fullWidth label="Task Description" variant="outlined" value={taskDescription} onChange={(e) => setTaskDescription(e.target.value)} multiline minRows={2} />
                        </Grid>
                        <Grid item xs={12} md={2} sx={{ display: 'flex' }}>
                            <Button fullWidth variant="contained" onClick={handleAssignTask} disabled={assigningTask || !selectedConsultantForTask || !taskDescription.trim()} startIcon={assigningTask ? <CircularProgress size={20} /> : <AddTaskIcon />}>
                                Assign
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
                
                {/* You could add other consultant-specific components here in the future,
                    like the leaderboard or analytics, filtered only for consultants.
                */}
                 <Grid item xs={12}>
                    {renderAnalytics()}
                </Grid>

            </motion.div>
        );
    }


    // --- MOVE useMemo CALL BEFORE EARLY RETURN ---
    // Memoize the filtered consultant list
    const filteredConsultants = useMemo(() => allUsers.filter(u => u.role === 'Consultant'), [allUsers]);

    if (loading && !firestoreInquiriesLoaded) {
        return ( <ThemeProvider theme={theme}><CssBaseline /><Box sx={{minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress size={60} /></Box></ThemeProvider> );
    }

    const currentDrawerWidth = isDesktop ? (isMiniDrawer ? DRAWER_WIDTH_MINI : DRAWER_WIDTH_FULL) : DRAWER_WIDTH_FULL;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', height: '100vh', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
                <AppBar position="fixed" sx={{
                    zIndex: theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ...(isDesktop && {
                        width: `calc(100% - ${currentDrawerWidth}px)`,
                        ml: `${currentDrawerWidth}px`,
                    }),
                }}>
                    <Toolbar sx={{ height: HEADER_HEIGHT, px: {xs: 1, sm:2}, gap: 1 }}>
                        <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, color: 'text.primary' }}><MenuIcon /></IconButton>
                        <Typography variant="h5" noWrap component="div" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                           {selectedMenu === "Inquiries" && viewingInquiryDetail ? `Inquiry: ${viewingInquiryDetail.id.substring(0,8)}...` : selectedMenu}
                        </Typography>
                        <Button variant="outlined" color="primary" startIcon={<DownloadOutlinedIcon />} onClick={() => window.open("/api/admin/report", "_blank")} sx={{ mr: 1.5, display: {xs: 'none', sm: 'inline-flex'} }}>Reports</Button>
                        <Tooltip title="Logout">
                            <IconButton onClick={handleLogout} sx={{ bgcolor: "error.main", color: "white", '&:hover': { bgcolor: "error.dark" } }}>
                                <LogoutIcon />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>

                <Drawer
                    variant={isDesktop ? "permanent" : "temporary"}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        width: currentDrawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: currentDrawerWidth,
                            boxSizing: 'border-box',
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            overflowX: 'hidden',
                        },
                    }}
                >
                    <Toolbar sx={{ height: HEADER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
                        {(isMiniDrawer && isDesktop) ? <DashboardIcon sx={{fontSize: '2.2rem', color: 'primary.main'}}/> :
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                           <Box component="img" src="https://placehold.co/100x40/eee/333?text=Logo" alt="Logo" sx={{ height: HEADER_HEIGHT * 0.5, borderRadius: '4px' }} onError={(e) => { e.target.src = 'https://placehold.co/100x40/eee/333?text=Logo'; }} />
                           <Typography variant="h6" sx={{fontWeight: 700, color: 'primary.main'}}>Admin</Typography>
                        </Box>
                        }
                    </Toolbar>
                    <Divider />
                    <List sx={{ p: 1 }}>
                        {menus.map(({ label, icon: Icon, count, action }) => (
                            <ListItem key={label} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    selected={selectedMenu === label && !viewingInquiryDetail}
                                    onClick={() => {
                                        if (action === "open-phone-rotations") {
                                            navigate("/omnichannel/phone-rotations", { state: { from: "/admin" } });
                                            if (!isDesktop) setDrawerOpen(false);
                                            return;
                                        }
                                        setSelectedMenu(label);
                                        setViewingInquiryDetail(null);
                                        if (!isDesktop) setDrawerOpen(false);
                                    }}
                                    sx={{ minHeight: 48, justifyContent: 'initial', px: 2.5, py: 1.5, mb: 0.5, borderRadius: 2,
                                        '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' }, '& .MuiSvgIcon-root': { color: 'primary.contrastText' }},
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: (isMiniDrawer && isDesktop) ? 'auto' : 3, justifyContent: 'center', color: 'inherit' }}><Icon /></ListItemIcon>
                                    <ListItemText primary={label} sx={{ opacity: (isMiniDrawer && isDesktop) ? 0 : 1 }} />
                                     {count > 0 && <Chip label={count} size="small" color="error" sx={{ ml: 'auto', pointerEvents: 'none', opacity: (isMiniDrawer && isDesktop) ? 0 : 1 }} />}
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Drawer>

                <Box component="main" sx={{
                    flexGrow: 1,
                    bgcolor: 'background.default',
                    p: { xs: 1, sm: 2, md: 3 },
                    minHeight: 0,
                    minWidth: 0,
                    height: '100vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Toolbar sx={{height: HEADER_HEIGHT}} />
                    <Box sx={{flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column'}}>
                        <AnimatePresence mode="wait">
                            {viewingInquiryDetail && selectedMenu === "Inquiries" ? (
                                <motion.div key="inquiryDetail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100%' }}>
                                    <AdminInquiryChatView
                                        inquiry={viewingInquiryDetail}
                                        onBack={() => setViewingInquiryDetail(null)}
                                        consultants={filteredConsultants} // Pass filtered list
                                        currentAppId={globalAppId}
                                        adminUser={user}
                                        onAdminMessageSent={() => showSnackbar("Message sent successfully!", "success")}
                                    />
                                </motion.div>
                            ) : selectedMenu === "User Management" ? <motion.div key="userManagement">{renderUserManagementView()}</motion.div>
                              : selectedMenu === "Consultants" ? <motion.div key="consultants">{renderConsultantsView()}</motion.div> 
                              : selectedMenu === "WhatsApp Lines" ? <motion.div key="whatsappLines"><WhatsAppLinesPage embedded /></motion.div>
                              : selectedMenu.startsWith("Reminders") ? <motion.div key="reminders">{renderInquiriesTable(inquiries.filter(i => i.reminder_24h_sent && !['closed_by_consultant', 'closed_by_user','closed_by_provider'].includes(i.status)), `Active 24h Reminders`)}</motion.div>
                              : selectedMenu.startsWith("Overdue") ? <motion.div key="overdue">{renderInquiriesTable(overdueInquiries, `Overdue Inquiries`)}</motion.div>
                              : selectedMenu === "Inquiries" ? <motion.div key="allInquiries">{renderInquiriesTable(inquiries, "All Inquiries")}</motion.div>
                              : selectedMenu === "Staff Directory" ? <motion.div key="staffDirectory"><StaffDirectorySection showSnackbar={showSnackbar} /></motion.div>
                              : selectedMenu === "Email Monitoring" ? <motion.div key="emailMonitoring"><MassEmailMonitoringSection showSnackbar={showSnackbar} /></motion.div>
                              : selectedMenu === "Email Signature" ? <motion.div key="emailSignature"><MassEmailCompanySignatureSection showSnackbar={showSnackbar} /></motion.div>
                              : selectedMenu === "Customers" ? <motion.div key="customers">{renderCustomersView()}</motion.div>
                              : selectedMenu === "Broadcast Budget" ? (
                                <motion.div key="broadcastBudget" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <BroadcastBudgetPanel
                                        title="Broadcast Budget"
                                        subtitle="Global shared cap enforced before any email or WhatsApp campaign is queued."
                                        channels={[broadcastBudget.email, broadcastBudget.whatsapp].filter(Boolean)}
                                        loading={broadcastBudgetLoading}
                                        isAdmin={Boolean(user?.is_admin) || ["admin", "superadmin", "marketing"].includes(String(user?.role || "").toLowerCase())}
                                        onBudgetSaved={fetchBroadcastBudget}
                                    />
                                </motion.div>
                              )
                              : selectedMenu === "Performance" ? <motion.div key="performance"><Performance /></motion.div>
                              : selectedMenu === "Analytics" ? <motion.div key="analytics"><Performance /></motion.div>
                              : (
                                <motion.div key="dashboardGrid">
                                    <Grid container spacing={GUTTER}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={GUTTER} justifyContent="center">
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <StatCard isLoading={loading} label="Answered Rate" value={`${metrics.rate}`} unit="%" icon={<QueryStatsIcon />} color={theme.palette.success.main} />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <StatCard isLoading={loading} label="Unanswered" value={metrics.unanswered} icon={<MailIcon />} color={theme.palette.error.main} />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <StatCard isLoading={loading} label="Total Inquiries" value={metrics.total} icon={<InboxIcon />} color={theme.palette.info.main} />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <StatCard isLoading={loading} label="Overdue" value={overdueInquiries.length} icon={<WarningIcon />} color={theme.palette.warning.main} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '20px' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: { xs: 'column', md: 'row' },
                                                        justifyContent: 'space-between',
                                                        alignItems: { xs: 'flex-start', md: 'center' },
                                                        gap: 1.5,
                                                        mb: 2.5,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                            WhatsApp Cloud API Monitor
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            Tracks outbound Cloud API message sends separately from inbound delivered/read callbacks.
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        color="primary"
                                                        variant="outlined"
                                                        label={
                                                            whatsAppUsageSummary.date
                                                                ? `${whatsAppUsageSummary.date} • ${whatsAppUsageSummary.timezone}`
                                                                : whatsAppUsageSummary.timezone
                                                        }
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </Box>

                                                {whatsAppUsageError ? (
                                                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                                                        {whatsAppUsageError}
                                                    </Alert>
                                                ) : null}

                                                <Grid container spacing={GUTTER} justifyContent="center">
                                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                                        <StatCard
                                                            isLoading={whatsAppUsageLoading}
                                                            label="Cloud API Sends Today"
                                                            value={`${whatsAppUsageSummary.outboundSendsToday} / ${whatsAppUsageSummary.dailyLimit}`}
                                                            icon={<SendIcon />}
                                                            color={theme.palette.primary.main}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                                        <StatCard
                                                            isLoading={whatsAppUsageLoading}
                                                            label="Estimated Headroom"
                                                            value={whatsAppUsageSummary.remainingOutboundCallsToday}
                                                            icon={<Speed />}
                                                            color={whatsAppUsageSummary.headroomColor}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                                        <StatCard
                                                            isLoading={whatsAppUsageLoading}
                                                            label="Delivered Callbacks"
                                                            value={whatsAppUsageSummary.deliveredCallbacksToday}
                                                            icon={<NotificationsIcon />}
                                                            color={theme.palette.info.main}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                                        <StatCard
                                                            isLoading={whatsAppUsageLoading}
                                                            label="Read Callbacks"
                                                            value={whatsAppUsageSummary.readCallbacksToday}
                                                            icon={<QueryStatsIcon />}
                                                            color={theme.palette.success.dark}
                                                        />
                                                    </Grid>
                                                </Grid>

                                                <Alert severity="info" sx={{ mt: 3, borderRadius: '12px' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                        Webhooks do not use outbound allowance.
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Today there are {whatsAppUsageSummary.statusCallbacksToday} delivered/read callbacks recorded. One outbound message can trigger both a delivered callback and a read callback, so callback totals can be higher than sends. This monitor estimates message-send usage only; template refreshes and other Graph API operations are not included.
                                                    </Typography>
                                                </Alert>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container spacing={GUTTER} alignItems="stretch">
                                                <Grid item xs={12} md={6}>
                                                    <ConsultantLeaderboard inquiries={inquiries} allUsers={allUsers} firestoreInquiriesLoaded={firestoreInquiriesLoaded} />
                                                </Grid>
                                                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                                                    {renderInquiriesTable(inquiries.slice(0, 5), "Recent Inquiries")}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </motion.div>
                                )}
                        </AnimatePresence>
                    </Box>
                </Box>

                {/* --- UPDATED DIALOGS --- */}

                <Dialog open={resetDialogOpen} onClose={closeResetDialog}>
                    <DialogTitle>Reset Password?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to reset the password for V <strong>{userToReset?.name}</strong>?
                            <br />
                            A new temporary password will be generated and sent to their email. This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeResetDialog} color="inherit">Cancel</Button>
                        <Button onClick={handleResetUserPassword} color="error" disabled={isResetting}>
                            {isResetting ? <CircularProgress size={22} /> : "Yes, Reset Password"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={editModalOpen} onClose={handleEditUserClose} maxWidth="sm" fullWidth>
                    <DialogTitle>Edit User</DialogTitle>
                    <DialogContent>
                        <TextField margin="dense" label="Name" type="text" fullWidth variant="outlined" value={editFormData.name} onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}/>
                        <TextField margin="dense" label="Email" type="email" fullWidth variant="outlined" value={editFormData.email} onChange={e => setEditFormData({ ...editFormData, email: e.target.value })}/>
                        <TextField
                            margin="dense"
                            label="Company Mailbox Email"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={editFormData.mailbox_email || ''}
                            onChange={e => setEditFormData({ ...editFormData, mailbox_email: e.target.value })}
                            helperText="Assigned mailbox address for IMAP/SMTP inbox integration"
                        />
                        <TextField
                            margin="dense" label="WhatsApp Number (e.g. +263771234567)" type="tel" fullWidth variant="outlined"
                            value={editFormData.whatsapp_number || ''}
                            onChange={e => setEditFormData({ ...editFormData, whatsapp_number: e.target.value })}
                            helperText="Used for escalation alerts and reminders"
                            InputProps={{ startAdornment: <span style={{ marginRight: 6, fontSize: 18 }}>💬</span> }}
                        />
                        <TextField margin="dense" label="New Password (optional)" type="password" fullWidth variant="outlined" value={editFormData.password} onChange={e => setEditFormData({ ...editFormData, password: e.target.value })} helperText="Leave blank to keep current password."/>
                        {/* --- EDIT ROLE DROPDOWN --- */}
                        <FormControl fullWidth margin="dense" required>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={editFormData.role}
                                onChange={e => setEditFormData({ ...editFormData, role: e.target.value })}
                                label="Role"
                            >
                                <MenuItem value="Consultant">Consultant</MenuItem>
                                <MenuItem value="Marketing">Marketing</MenuItem>
                                <MenuItem value="SuperAdmin">Super Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleEditUserClose}>Cancel</Button>
                        <Button onClick={handleEditUserSave} variant="contained">Save Changes</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteModalOpen} onClose={handleDeleteUserClose}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete the user: <strong>{deletingUser?.name}</strong>? This cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleDeleteUserClose}>Cancel</Button>
                        <Button onClick={handleDeleteUserConfirm} color="error" variant="contained">Delete</Button>
                    </DialogActions>
                </Dialog>

                <AssignInquiryDialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} onAssign={handleAssignInquiry} inquiry={selectedInquiryForDialog} />

                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '12px', boxShadow: 6 }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
}

function StaffDirectorySection({ showSnackbar }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [testPhone, setTestPhone] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [backfillLoading, setBackfillLoading] = useState(false);
    const [form, setForm] = useState({
        full_name: '', primary_email: '', primary_phone: '',
        alternate_emails: '', alternate_phones: '',
        department: '', job_title: '', is_active: true, detection_notes: '',
    });

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/staff-directory');
            setEntries(res.data.data || res.data);
        } catch {
            showSnackbar('Failed to load staff directory', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEntries(); }, []);

    const openAdd = () => {
        setEditingEntry(null);
        setForm({ full_name: '', primary_email: '', primary_phone: '', alternate_emails: '', alternate_phones: '', department: '', job_title: '', is_active: true, detection_notes: '' });
        setDialogOpen(true);
    };

    const openEdit = (entry) => {
        setEditingEntry(entry);
        setForm({
            full_name: entry.full_name || '',
            primary_email: entry.primary_email || '',
            primary_phone: entry.primary_phone || '',
            alternate_emails: (entry.alternate_emails || []).join(', '),
            alternate_phones: (entry.alternate_phones || []).join(', '),
            department: entry.department || '',
            job_title: entry.job_title || '',
            is_active: entry.is_active !== false,
            detection_notes: entry.detection_notes || '',
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const payload = {
            ...form,
            alternate_emails: form.alternate_emails ? form.alternate_emails.split(',').map(e => e.trim()).filter(Boolean) : [],
            alternate_phones: form.alternate_phones ? form.alternate_phones.split(',').map(p => p.trim()).filter(Boolean) : [],
        };
        try {
            if (editingEntry) {
                await api.put(`/admin/staff-directory/${editingEntry.id}`, payload);
                showSnackbar('Staff member updated', 'success');
            } else {
                await api.post('/admin/staff-directory', payload);
                showSnackbar('Staff member added', 'success');
            }
            setDialogOpen(false);
            fetchEntries();
        } catch (err) {
            showSnackbar(err?.response?.data?.message || 'Save failed', 'error');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/admin/staff-directory/${deleteTarget.id}`);
            showSnackbar('Staff member removed', 'success');
            setDeleteTarget(null);
            fetchEntries();
        } catch {
            showSnackbar('Delete failed', 'error');
        }
    };

    const handleTestDetection = async () => {
        if (!testEmail && !testPhone) return;
        try {
            const res = await api.post('/admin/staff-directory/test-detection', { email: testEmail, phone: testPhone });
            setTestResult(res.data);
        } catch {
            showSnackbar('Test failed', 'error');
        }
    };

    const handleBackfill = async () => {
        setBackfillLoading(true);
        try {
            const res = await api.post('/admin/staff-directory/backfill');
            showSnackbar(res.data.message || 'Backfill queued', 'success');
        } catch {
            showSnackbar('Backfill failed', 'error');
        } finally {
            setBackfillLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>Staff Directory</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" color="warning" size="small" onClick={handleBackfill} disabled={backfillLoading}>
                        {backfillLoading ? 'Queuing...' : 'Scan Contacts'}
                    </Button>
                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openAdd}>Add Staff Member</Button>
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Test Internal Detection</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" label="Email" value={testEmail} onChange={e => setTestEmail(e.target.value)} sx={{ minWidth: 220 }} />
                    <TextField size="small" label="Phone" value={testPhone} onChange={e => setTestPhone(e.target.value)} sx={{ minWidth: 160 }} />
                    <Button variant="outlined" onClick={handleTestDetection}>Check</Button>
                    {testResult && (
                        <Chip
                            icon={testResult.is_internal ? <CheckCircleIcon /> : <CancelIcon />}
                            label={testResult.is_internal ? `Internal — ${testResult.reason}` : 'External (not staff)'}
                            color={testResult.is_internal ? 'success' : 'default'}
                        />
                    )}
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Primary Email</strong></TableCell>
                                <TableCell><strong>Phone</strong></TableCell>
                                <TableCell><strong>Department</strong></TableCell>
                                <TableCell><strong>Linked User</strong></TableCell>
                                <TableCell><strong>Active</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No staff members added yet.</TableCell></TableRow>
                            ) : entries.map(entry => (
                                <TableRow key={entry.id} hover>
                                    <TableCell>{entry.full_name}</TableCell>
                                    <TableCell>{entry.primary_email || '—'}</TableCell>
                                    <TableCell>{entry.primary_phone || '—'}</TableCell>
                                    <TableCell>{entry.department || '—'}</TableCell>
                                    <TableCell>{entry.user?.name || '—'}</TableCell>
                                    <TableCell>
                                        <Chip label={entry.is_active ? 'Active' : 'Inactive'} color={entry.is_active ? 'success' : 'default'} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(entry)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(entry)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingEntry ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField label="Full Name *" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} fullWidth size="small" />
                    <TextField label="Primary Email" value={form.primary_email} onChange={e => setForm(f => ({ ...f, primary_email: e.target.value }))} fullWidth size="small" />
                    <TextField label="Primary Phone" value={form.primary_phone} onChange={e => setForm(f => ({ ...f, primary_phone: e.target.value }))} fullWidth size="small" />
                    <TextField label="Alternate Emails (comma-separated)" value={form.alternate_emails} onChange={e => setForm(f => ({ ...f, alternate_emails: e.target.value }))} fullWidth size="small" />
                    <TextField label="Alternate Phones (comma-separated)" value={form.alternate_phones} onChange={e => setForm(f => ({ ...f, alternate_phones: e.target.value }))} fullWidth size="small" />
                    <TextField label="Department" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} fullWidth size="small" />
                    <TextField label="Job Title" value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} fullWidth size="small" />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.value }))}>
                            <MenuItem value={true}>Active</MenuItem>
                            <MenuItem value={false}>Inactive</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Detection Notes" value={form.detection_notes} onChange={e => setForm(f => ({ ...f, detection_notes: e.target.value }))} fullWidth size="small" multiline rows={2} />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editingEntry ? 'Update' : 'Add'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Remove Staff Member?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove <strong>{deleteTarget?.full_name}</strong> from the staff directory? This will not unmark contacts already flagged as internal.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Remove</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
